import { Injectable } from '@angular/core';
import Point from '@arcgis/core/geometry/Point';
import Extent from '@arcgis/core/geometry/Extent';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';

/**
 * Spatial reference WKIDs used in the application
 */
export type SpatialReferenceWkid = 4326 | 3857 | 102100;

/**
 * Coordinate interface for lat/lng points
 */
export interface Coordinate {
  latitude: number;
  longitude: number;
}

/**
 * Bounding box interface for extent calculations
 */
export interface BoundingBox {
  xmin: number;
  ymin: number;
  xmax: number;
  ymax: number;
}

/**
 * GeometryService
 *
 * Provides geometry utilities for map operations including:
 * - Coordinate projections (WGS84 <-> Web Mercator)
 * - Location key generation for grouping items at same location
 * - Bounding extent calculations
 * - Centroid calculations
 *
 * Part of Phase 2.1 of the map maintainability refactor.
 */
@Injectable({ providedIn: 'root' })
export class GeometryService {
  /** WGS84 (lat/lon) spatial reference */
  private readonly WGS84: SpatialReferenceWkid = 4326;

  /** Web Mercator spatial reference (Esri maps default) */
  private readonly WEB_MERCATOR: SpatialReferenceWkid = 102100;

  /**
   * Convert a point to Web Mercator projection (for Esri maps)
   * @param point - Point in WGS84 or other projection
   * @returns Point in Web Mercator projection
   */
  toWebMercator(point: Point): Point {
    if (
      point.spatialReference?.wkid === this.WEB_MERCATOR ||
      point.spatialReference?.wkid === 3857
    ) {
      return point;
    }
    return webMercatorUtils.geographicToWebMercator(point) as Point;
  }

  /**
   * Convert a point to WGS84 (lat/lon)
   * @param point - Point in Web Mercator or other projection
   * @returns Point in WGS84
   */
  toWGS84(point: Point): Point {
    if (point.spatialReference?.wkid === this.WGS84) {
      return point;
    }
    return webMercatorUtils.webMercatorToGeographic(point) as Point;
  }

  /**
   * Create an Esri Point from lat/lon
   * @param latitude - Latitude coordinate
   * @param longitude - Longitude coordinate
   * @param wkid - Spatial reference WKID (default: 4326)
   */
  createPoint(
    latitude: number,
    longitude: number,
    wkid: SpatialReferenceWkid = 4326
  ): Point {
    return new Point({
      latitude,
      longitude,
      spatialReference: { wkid }
    });
  }

  /**
   * Check if two points are at the same location (within tolerance)
   * Uses specified decimal places for precision
   * @param lat1 - First point latitude
   * @param lon1 - First point longitude
   * @param lat2 - Second point latitude
   * @param lon2 - Second point longitude
   * @param precision - Decimal places for comparison (default: 6 = ~0.1 meter)
   * @returns true if points are at the same location
   */
  isSameLocation(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
    precision: number = 6
  ): boolean {
    const lat1Fixed = parseFloat(lat1.toFixed(precision));
    const lon1Fixed = parseFloat(lon1.toFixed(precision));
    const lat2Fixed = parseFloat(lat2.toFixed(precision));
    const lon2Fixed = parseFloat(lon2.toFixed(precision));
    return lat1Fixed === lat2Fixed && lon1Fixed === lon2Fixed;
  }

  /**
   * Create a location key for grouping items at same location
   * Uses a consistent format for map keys
   * @param latitude - Latitude coordinate
   * @param longitude - Longitude coordinate
   * @param precision - Decimal places (default: 6)
   * @returns Location key string in format "lat,lng"
   */
  createLocationKey(
    latitude: number,
    longitude: number,
    precision: number = 6
  ): string {
    return `${latitude.toFixed(precision)},${longitude.toFixed(precision)}`;
  }

  /**
   * Calculate center point (centroid) from array of coordinates
   * @param points - Array of coordinate objects
   * @returns Center point or null if no points provided
   */
  calculateCentroid(points: Coordinate[]): Coordinate | null {
    if (!points || !points.length) {
      return null;
    }

    const sum = points.reduce(
      (acc, p) => ({
        lat: acc.lat + p.latitude,
        lon: acc.lon + p.longitude
      }),
      { lat: 0, lon: 0 }
    );

    return {
      latitude: sum.lat / points.length,
      longitude: sum.lon / points.length
    };
  }

  /**
   * Calculate bounding extent from array of coordinates
   * Returns null for single point (use direct center/zoom instead)
   * @param coordinates - Array of coordinates with latitude/longitude
   * @returns Esri Extent object or null if invalid
   */
  calculateBoundingExtent(coordinates: Coordinate[]): Extent | null {
    if (!coordinates || !coordinates.length) {
      return null;
    }

    // For single coordinate, return null to use the fallback single point logic
    if (coordinates.length === 1) {
      return null;
    }

    const bounds = coordinates.reduce(
      (acc, coord) => ({
        xmin: Math.min(acc.xmin, coord.longitude),
        ymin: Math.min(acc.ymin, coord.latitude),
        xmax: Math.max(acc.xmax, coord.longitude),
        ymax: Math.max(acc.ymax, coord.latitude)
      }),
      {
        xmin: Number.MAX_SAFE_INTEGER,
        ymin: Number.MAX_SAFE_INTEGER,
        xmax: Number.MIN_SAFE_INTEGER,
        ymax: Number.MIN_SAFE_INTEGER
      }
    );

    // Validate the extent
    if (
      bounds.xmin < bounds.xmax &&
      bounds.ymin < bounds.ymax &&
      !isNaN(bounds.xmin) &&
      !isNaN(bounds.ymin) &&
      isFinite(bounds.xmin) &&
      isFinite(bounds.xmax) &&
      isFinite(bounds.ymin) &&
      isFinite(bounds.ymax)
    ) {
      return new Extent(bounds);
    }

    return null;
  }

  /**
   * Calculate bounding box (raw values) from coordinates
   * Useful when you need the raw values before creating an Extent
   * @param coordinates - Array of coordinates
   * @returns BoundingBox or null if invalid
   */
  calculateBoundingBox(coordinates: Coordinate[]): BoundingBox | null {
    if (!coordinates || coordinates.length === 0) {
      return null;
    }

    const bounds = coordinates.reduce(
      (acc, coord) => ({
        xmin: Math.min(acc.xmin, coord.longitude),
        ymin: Math.min(acc.ymin, coord.latitude),
        xmax: Math.max(acc.xmax, coord.longitude),
        ymax: Math.max(acc.ymax, coord.latitude)
      }),
      {
        xmin: Number.MAX_SAFE_INTEGER,
        ymin: Number.MAX_SAFE_INTEGER,
        xmax: Number.MIN_SAFE_INTEGER,
        ymax: Number.MIN_SAFE_INTEGER
      }
    );

    // Validate bounds
    if (
      isFinite(bounds.xmin) &&
      isFinite(bounds.xmax) &&
      isFinite(bounds.ymin) &&
      isFinite(bounds.ymax)
    ) {
      return bounds;
    }

    return null;
  }

  /**
   * Calculate distance between two points in meters (Haversine formula)
   * @param lat1 - First point latitude
   * @param lon1 - First point longitude
   * @param lat2 - Second point latitude
   * @param lon2 - Second point longitude
   * @returns Distance in meters
   */
  calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371000; // Earth's radius in meters
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) *
        Math.cos(this.toRadians(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Check if a coordinate is valid (not null, not NaN, within valid ranges)
   * @param latitude - Latitude to validate
   * @param longitude - Longitude to validate
   * @returns true if coordinates are valid
   */
  isValidCoordinate(latitude: number | null | undefined, longitude: number | null | undefined): boolean {
    if (latitude === null || latitude === undefined || longitude === null || longitude === undefined) {
      return false;
    }

    if (isNaN(latitude) || isNaN(longitude)) {
      return false;
    }

    // Valid latitude range: -90 to 90
    // Valid longitude range: -180 to 180
    return (
      latitude >= -90 &&
      latitude <= 90 &&
      longitude >= -180 &&
      longitude <= 180
    );
  }

  /**
   * Convert degrees to radians
   */
  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }
}
